import { dayjs } from '#gp/app-common/util/dayjs';
import { gpHome } from '../app';
import { IProjectService, IProject } from '../../app-common/services/projects-service';
import { templateDeleteFailureMessage } from '../../app-common/services/project-delete-outcome';
import { ConfirmationDialogService, gpConfirmationDialogButtons } from '../../app-common/components/confirmation-dialog';
import { IRouteAuthService, IRouteAuthServiceEvents } from '../services/route-auth-service';
import { formatRelativeTimeInPast } from '../../app-common/util/dateFormatting';
import { arraySortBy } from '#gp/app-common/util/arrays';
import { sortKeyCompareOption } from '#gp/app-common/util/text-collation';

interface ITemplatesControllerScope extends ng.IScope {
    templates: SimpleProject[];
    templatesLoaded: boolean;
    addProject(): void;
    deleteTemplate(p: SimpleProject): void;
    gotoSubscriptions(): void;
    gotoAccounts(): void;
}

class SimpleProject {
    id: string;
    name: string;
    description: string | null;
    lastEdited: string | null;
    lastEditedUserEmail: string;
    isShared: boolean;
    accessLevel: string;
    thumbnailUrl: string;
}

class TemplatesController {
    static $inject = ['$scope', '$location', 'projectsService', ConfirmationDialogService.serviceName, 'gpRouteAuthService'];
    private readonly authEventOptions: IRouteAuthServiceEvents = {
        loggedIn: () => this.updateTemplatesList(),
        loggedOut: () => this.$location.path("/login"),
    };

    constructor(
        public $scope: ITemplatesControllerScope,
        private $location: ng.ILocationService,
        public projectService: IProjectService,
        private gpConfirmationDialogService: ConfirmationDialogService,
        private gpRouteAuthService: IRouteAuthService) {

        let me = this;

        $scope.templates = [];
        $scope.templatesLoaded = false;
        $scope.deleteTemplate = (p) => me.deleteTemplate(p);
        $scope.addProject = () => me.addProject();

        $scope.gotoSubscriptions = () => $location.path('/subscribe');
        $scope.gotoAccounts = () => $location.path('/account');

        gpRouteAuthService.register(this.authEventOptions);
        $scope.$on('$destroy', () => this.gpRouteAuthService.deregister(this.authEventOptions));
    }

    addProject() {
        this.$location.path("/plans/new");
    }

    updateTemplatesList() {
        this.projectService.getTemplatesChecked().then(fetchResult => {
            let templates: IProject[] = fetchResult.projects;

            let i = templates.map(p => ({ p: p, when: p.lastEditedUTC ? p.lastEditedUTC.unix() : 0 }));

            // Sorted one key at a time, least significant first: arraySortBy is a
            // stable sort, so each pass preserves the order set by the previous one.
            let byName = arraySortBy(i, p => p.p.name, sortKeyCompareOption);
            templates = arraySortBy(byName, p => p.when, { ...sortKeyCompareOption, direction: 'desc' })
                .map(p => p.p);

            this.$scope.templates = templates.map(p => ({
                id: p.id || '',
                name: p.name,
                description: p.description,
                lastEdited: this.toLastEditedText(p.lastEditedUTC),
                lastEditedUserEmail: p.lastEditedUserEmail,
                isShared: p.isShared,
                accessLevel: p.accessLevel,
                // Versioned by last-saved time so a resaved template defeats
                // the browser's cached image.
                thumbnailUrl: p.thumbnailUrl
                    ? p.thumbnailUrl + (p.thumbnailUrl.indexOf('?') >= 0 ? '&' : '?') + 'v=' + (p.lastEditedUTC ? p.lastEditedUTC.unix() : 0)
                    : p.thumbnailUrl,
            }));

            // Only claim "no templates yet" when the server actually said so;
            // a failed fetch also yields an empty list.
            this.$scope.templatesLoaded = fetchResult.succeeded;
        });
    }

    private toLastEditedText(utc: dayjs.Dayjs | null) {
        if (utc) {
            return "Last saved: " + formatRelativeTimeInPast(utc, true);
        } else {
            return null;
        }
    }

    deleteTemplate(p: SimpleProject) {
        let dialogResult = this.gpConfirmationDialogService.show(
            {
                title: "Delete template",
                mainInstruction: "Are you sure you want to delete " + p.name + "?",
                buttons: [{ caption: "Delete", result: "delete" }, { caption: "Cancel", result: "cancel" }],
            });

        dialogResult.then((result: string) => {
            if (result !== "delete") {
                return;
            }

            this.projectService.deleteTemplateByIdChecked(p.id).then(deleteResult => {
                this.updateTemplatesList();

                if (!deleteResult.succeeded) {
                    this.gpConfirmationDialogService.show({
                        title: "Template not deleted",
                        mainInstruction: templateDeleteFailureMessage(p.name, deleteResult.reason),
                        buttons: [gpConfirmationDialogButtons.Ok],
                    });
                }
            });
        });
    }
}

gpHome.controller('templatesController', TemplatesController);
